import { baseApiSlice } from '../../commons/base-api.config';
import { HTTP_METHODS } from '../../constants/http';
import {
    API_DOWNLOAD_COUNT,
    API_PROJECT,
    API_PROJECT_DUPLICATE,
    API_UPLOAD_ASSET,
} from '../../constants/api';
import { IGetUploadedAssetsResponse } from '../../interfaces/assets.interface';
import {
    ICreateOrUpdateProjectResponse,
    IGetProjectsResponse,
    IProjectTemplate,
} from '../../interfaces/project.interface';
import { IPaginationParams } from '../../interfaces/common.interface';

export const editorApiSlice = baseApiSlice.injectEndpoints({
    endpoints(builder) {
        return {
            getProjects: builder.query<IGetProjectsResponse, IPaginationParams>({
                query(params) {
                    return {
                        url: `${API_PROJECT}?page=${
                            params.page !== undefined ? params.page : 1
                        }&size=15&brandId=${params.brandId}`,
                        method: HTTP_METHODS.GET,
                    };
                },
                providesTags: ['allProjects'],
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            getProjectById: builder.query<any, number>({
                query(id) {
                    return {
                        url: `${API_PROJECT}/${id}`,
                        method: HTTP_METHODS.GET,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            getUploadedAssets: builder.query<IGetUploadedAssetsResponse, IPaginationParams>({
                query(params) {
                    return {
                        url: `${API_UPLOAD_ASSET}?page=${params.page !== undefined ? params.page : 1}&size=15&brandId=${params.brandId}`,
                        method: HTTP_METHODS.GET,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            uploadAsset: builder.mutation({
                query: (data) => {
                    const formData = new FormData();
                    formData.append('asset', data.image as File);
                    formData.append('brandId', data.brandId);
                    return {
                        url: API_UPLOAD_ASSET,
                        method: HTTP_METHODS.POST,
                        body: formData,
                    };
                },
                transformResponse: ({ data }: any) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            deleteAsset: builder.mutation({
                query: (id) => {
                    return {
                        url: `${API_UPLOAD_ASSET}/${id}`,
                        method: HTTP_METHODS.DELETE,
                    };
                },
                transformResponse: (data: { result: any }) => data?.result,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            createOrUpdateProject: builder.mutation<
                ICreateOrUpdateProjectResponse,
                IProjectTemplate
            >({
                query: (data) => {
                    return {
                        url: API_PROJECT,
                        method: HTTP_METHODS.PATCH,
                        body: data,
                    };
                },
                transformResponse: (res: any) => res.data,
                transformErrorResponse: (response) => response,
            }),
            deleteProject: builder.mutation({
                query: (id) => {
                    return {
                        url: `${API_PROJECT}/${id}`,
                        method: HTTP_METHODS.DELETE,
                    };
                },
                invalidatesTags: ['allProjects'],
                transformResponse: (data: { result: any }) => data?.result,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            duplicateProject: builder.mutation({
                query: ({ id, data }) => {
                    return {
                        url: `${API_PROJECT_DUPLICATE}/${id}`,
                        method: HTTP_METHODS.PUT,
                        body: data,
                    };
                },
                invalidatesTags: ['allProjects'],
                transformResponse: (data) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            renameProject: builder.mutation({
                query: ({ id, data }) => {
                    return {
                        url: `${API_PROJECT}/${id}`,
                        method: HTTP_METHODS.PUT,
                        body: data,
                    };
                },
                invalidatesTags: ['allProjects'],
                transformResponse: (data) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
            downloadCount: builder.mutation({
                query: (data) => {
                    return {
                        url: API_DOWNLOAD_COUNT,
                        method: HTTP_METHODS.POST,
                        body: data,
                    };
                },
                transformResponse: (data) => data,
                transformErrorResponse: (response: { status: string | number }) => response.status,
            }),
        };
    },
});

export const {
    useLazyGetProjectsQuery,
    useLazyGetProjectByIdQuery,
    useCreateOrUpdateProjectMutation,
    useDeleteProjectMutation,
    useDuplicateProjectMutation,
    useRenameProjectMutation,
    useLazyGetUploadedAssetsQuery,
    useUploadAssetMutation,
    useDownloadCountMutation,
    useDeleteAssetMutation,
} = editorApiSlice;
